<!--
  NewsletterEditor.svelte — Dedicated create / edit page for a single newsletter.

  Provides a full-page form with token insertion toolbar, live inline preview
  with mock variable substitution, and master template selection. Reached via
  "New Newsletter" or "Edit" from AdminNewsletters.
-->
<script lang="ts">
	import { onMount, tick } from 'svelte';
	import { Button, Heading, Input, Label, Spinner, Badge } from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import AbstractDropdown from '../../components/AbstractDropdown.svelte';
	import { push } from '../../abstract-spa-router';
	import {
		getNewsletter,
		createNewsletter,
		updateNewsletter,
		listEmailTemplates,
		previewNewsletter,
		listApplications
	} from '../../AppNetwork';
	import type { Newsletter, EmailTemplate, Application } from '../../AppTypes';
	import { toastSuccess, toastError } from '../../toast';
	import { t, t2 } from '../../i18n';

	interface Props {
		/** Route params — `:id` is present when editing, absent when creating. */
		params?: { id?: string };
	}

	let { params = {} }: Props = $props();

	// ── Derived state ─────────────────────────────────────────────

	/** Parsed newsletter ID (0 = create mode). */
	let newsletterId = $derived(params.id ? parseInt(params.id) : 0);
	/** True when editing an existing newsletter. */
	let isEditing = $derived(newsletterId > 0);

	// ── Loading / error ───────────────────────────────────────────

	/** Initial data loading flag. */
	let loading = $state(true);
	/** Save in progress flag. */
	let saving = $state(false);
	/** Error banner. */
	let error = $state('');

	// ── Form fields ───────────────────────────────────────────────

	let formName = $state('');
	let formSubject = $state('');
	let formHtml = $state('');
	let formText = $state('');
	let formAudienceType: 'all' | 'filtered' = $state('all');
	let formWaveSize = $state(50);
	let formWaveInterval = $state(60);
	let formMasterTemplateId: number | string = $state('');

	// ── Master templates ──────────────────────────────────────────

	/** Available master email templates. */
	let masterTemplates: EmailTemplate[] = $state([]);
	/** Dropdown items for the master template dropdown. */
	let masterTemplateOptions = $derived([
		{ value: '', name: t2('No master template'), icon: 'fa-solid fa-ban' },
		...masterTemplates.map((t) => ({ value: String(t.id), name: t.templateName, icon: 'fa-solid fa-file-code' }))
	]);

	// ── Audience options ──────────────────────────────────────────

	/** Audience type dropdown items. */
	const audienceOptions = [
		{ value: 'all', name: t2('All Users (active + imported)'), icon: 'fa-solid fa-users' },
		{ value: 'filtered', name: t2('Filtered by Application'), icon: 'fa-solid fa-filter' }
	];

	/** All available applications for the filtered audience multi-select. */
	let allApplications: Application[] = $state([]);
	/** Selected application IDs when audienceType is 'filtered'. */
	let selectedAppIds: number[] = $state([]);

	// ── Tokens / placeholders ─────────────────────────────────────

	/** Available mail-merge tokens and their preview mock values. */
	const tokens: { key: string; label: string; preview: string }[] = [
		{ key: 'FIRST_NAME', label: t2('First Name'), preview: 'Jane' },
		{ key: 'LAST_NAME', label: t2('Last Name'), preview: 'Doe' },
		{ key: 'EMAIL', label: t2('Email'), preview: 'jane.doe@example.com' },
		{ key: 'UNSUBSCRIBE_URL', label: t2('Unsubscribe URL'), preview: '/track/unsubscribe/preview' }
	];

	/** Which textarea is currently focused — determines where tokens are inserted. */
	let activeField: 'subject' | 'html' | 'text' = $state('html');

	/** Refs to textarea / input DOM elements for cursor-position insertion. */
	let subjectEl: HTMLInputElement | undefined = $state();
	let htmlEl: HTMLTextAreaElement | undefined = $state();
	let textEl: HTMLTextAreaElement | undefined = $state();

	/**
	 * Inserts `{{KEY}}` at the cursor position in the currently active field,
	 * or appends if there is no selection.
	 */
	function insertToken(key: string): void {
		const tag = `{{${key}}}`;
		const el = activeField === 'subject' ? subjectEl : activeField === 'text' ? textEl : htmlEl;

		/** If no DOM element ref is available, fall back to string concatenation. */
		if (!el) {
			/* Fallback: just append to the active field value. */
			if (activeField === 'subject') formSubject += tag;
			else if (activeField === 'text') formText += tag;
			else formHtml += tag;
			return;
		}

		const start = el.selectionStart ?? el.value.length;
		const end = el.selectionEnd ?? el.value.length;
		const before = el.value.slice(0, start);
		const after = el.value.slice(end);
		const newValue = before + tag + after;

		/* Update the bound variable. */
		if (activeField === 'subject') formSubject = newValue;
		else if (activeField === 'text') formText = newValue;
		else formHtml = newValue;

		/* Restore cursor after Svelte re-renders. */
		const cursorPos = start + tag.length;
		tick().then(() => {
			el.focus();
			el.setSelectionRange(cursorPos, cursorPos);
		});
	}

	// ── Inline preview ────────────────────────────────────────────

	/** Replaces `{{TOKEN}}` placeholders with mock values for the preview. */
	function substitutePreview(text: string): string {
		return text.replace(/\{\{(\w+)\}\}/g, (_match, key: string) => {
			const t = tokens.find((tok) => tok.key === key);
			return t ? t.preview : `{{${key}}}`;
		});
	}

	/** Reactive preview of the subject line with mock substitutions. */
	let previewSubject = $derived(substitutePreview(formSubject));

	/** The currently selected master template object, if any. */
	let selectedMaster = $derived(masterTemplates.find((t) => String(t.id) === String(formMasterTemplateId)));

	/**
	 * Reactive preview of the HTML body with mock substitutions.
	 * When a master template is selected, the body is injected into the
	 * master's `{{BODY}}` slot so the preview matches the final email.
	 */
	let previewHtmlContent = $derived((() => {
		const body = substitutePreview(formHtml);
		if (selectedMaster?.htmlContent) {
			return substitutePreview(selectedMaster.htmlContent.replace(/\{\{BODY\}\}/g, body));
		}
		return body;
	})());

	// ── Server preview (with master template) ─────────────────────

	/** Full server-rendered preview HTML. */
	let serverPreviewHtml = $state('');
	/** Server preview loading state. */
	let serverPreviewLoading = $state(false);

	// ── Data loading ──────────────────────────────────────────────

	/** Loads the newsletter (edit mode) and master templates. */
	async function loadData(): Promise<void> {
		loading = true;
		error = '';
		/** Fetch master templates, applications, and (if editing) the newsletter itself. */
		try {
			/* Load master templates. */
			const templates = await listEmailTemplates();
			masterTemplates = templates.filter((t) => t.templateType === 'master');

			/* Load applications for filtered audience. */
			try {
				allApplications = await listApplications();
			} catch {
				/* non-critical */
			}

			/* If editing, load the newsletter. */
			if (isEditing) {
				const nl = await getNewsletter(newsletterId);
				formName = nl.name;
				formSubject = nl.subject;
				formHtml = nl.htmlContent;
				formText = nl.textContent || '';
				formAudienceType = nl.audienceType;
				if (nl.audienceFilter && Array.isArray((nl.audienceFilter as any).applicationIds)) {
					selectedAppIds = (nl.audienceFilter as any).applicationIds;
				}
				formWaveSize = nl.waveSize;
				formWaveInterval = nl.waveIntervalSeconds;
				formMasterTemplateId = nl.masterTemplate?.id ? String(nl.masterTemplate.id) : '';
			}
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load data');
		} finally {
			loading = false;
		}
	}

	// ── Save ──────────────────────────────────────────────────────

	/** Creates or updates the newsletter and navigates back to the list. */
	async function save(): Promise<void> {
		saving = true;
		/** Build the payload and create or update the newsletter on the server. */
		try {
			const payload: Record<string, unknown> = {
				name: formName,
				subject: formSubject,
				htmlContent: formHtml,
				textContent: formText || null,
				audienceType: formAudienceType,
				audienceFilter: formAudienceType === 'filtered' ? { applicationIds: selectedAppIds } : null,
				waveSize: formWaveSize,
				waveIntervalSeconds: formWaveInterval,
				masterTemplateId: formMasterTemplateId ? Number(formMasterTemplateId) : null
			};
			if (isEditing) {
				await updateNewsletter(newsletterId, payload as Partial<Newsletter>);
				toastSuccess(t2('Newsletter updated.'));
			} else {
				await createNewsletter(payload as Partial<Newsletter>);
				toastSuccess(t2('Newsletter created.'));
			}
			push('/Newsletters');
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to save newsletter'));
		} finally {
			saving = false;
		}
	}

	// ── Server-rendered full preview ──────────────────────────────

	/** Loads a server-rendered preview (only available for existing newsletters). */
	async function loadServerPreview(): Promise<void> {
		if (!isEditing) return;
		serverPreviewLoading = true;
		/** Request a full server-rendered preview with the selected master template. */
		try {
			const masterId = formMasterTemplateId ? Number(formMasterTemplateId) : undefined;
			const result = await previewNewsletter(newsletterId, masterId);
			serverPreviewHtml = result.html;
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to load server preview'));
		} finally {
			serverPreviewLoading = false;
		}
	}

	// ── Lifecycle ─────────────────────────────────────────────────

	onMount(loadData);
</script>

<!-- Back link + Title -->
<div class="mb-6 flex items-center gap-3">
	<Button size="sm" color="alternative" onclick={() => push('/Newsletters')}>
		<i class="fa-solid fa-arrow-left mr-1 h-4 w-4"></i> {$t("Back")}
	</Button>
	<Heading tag="h2">{isEditing ? $t('Edit Newsletter') : $t('New Newsletter')}</Heading>
</div>

{#if error}
	<div class="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900 dark:text-red-300">
		{error}
		<button class="ml-2 underline" onclick={() => (error = '')}>{$t("dismiss")}</button>
	</div>
{/if}

{#if loading}
	<div class="flex justify-center py-12"><Spinner /></div>
{:else}
	<div class="grid grid-cols-1 gap-6 xl:grid-cols-2">
		<!-- ─── Left column: Form ──────────────────────────────── -->
		<div class="space-y-5">
			<!-- Campaign info -->
			<AbstractCard class="max-w-none">
				<Heading tag="h5" class="mb-4">
					<i class="fa-solid fa-bullhorn me-2"></i>{$t("Campaign Details")}
				</Heading>
				<div class="space-y-4">
					<div>
						<Label for="nl-name" class="mb-1">{$t("Campaign Name")}</Label>
						<Input id="nl-name" placeholder={$t("e.g. January Newsletter")} bind:value={formName} required />
					</div>
					<div>
						<Label for="nl-subject" class="mb-1">{$t("Subject Line")}</Label>
						<input
							id="nl-subject"
							type="text"
							class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
							placeholder="e.g. Hello Jane!"
							bind:value={formSubject}
							required
							bind:this={subjectEl}
							onfocus={() => (activeField = 'subject')}
						/>
					</div>
				</div>
			</AbstractCard>

			<!-- Delivery settings -->
			<AbstractCard class="max-w-none">
				<Heading tag="h5" class="mb-4">
					<i class="fa-solid fa-paper-plane me-2"></i>{$t("Delivery")}
				</Heading>
				<div class="space-y-4">
					<div>
						<Label class="mb-1">{$t("Audience")}</Label>
						<AbstractDropdown items={audienceOptions} value={formAudienceType} onchange={(v) => { formAudienceType = v as 'all' | 'filtered'; }} />
					</div>
					{#if formAudienceType === 'filtered'}
						<div>
							<Label class="mb-1">{$t("Applications")}</Label>
							{#if allApplications.length === 0}
								<p class="text-sm italic text-gray-400">{$t("No applications configured.")}</p>
							{:else}
								<div class="max-h-40 space-y-1 overflow-y-auto rounded border p-2 dark:border-gray-600">
									{#each allApplications as app (app.id)}
										<label class="flex cursor-pointer items-center gap-2 text-sm dark:text-white">
											<input
												type="checkbox"
												checked={selectedAppIds.includes(app.id)}
												onchange={() => {
													if (selectedAppIds.includes(app.id)) {
														selectedAppIds = selectedAppIds.filter((i) => i !== app.id);
													} else {
														selectedAppIds = [...selectedAppIds, app.id];
													}
												}}
												class="rounded"
											/>
											{app.applicationName}
										</label>
									{/each}
								</div>
							{/if}
						</div>
					{/if}
					<div>
						<Label class="mb-1">{$t("Master Template")}</Label>
						<AbstractDropdown items={masterTemplateOptions} bind:value={formMasterTemplateId} />
					</div>
					<div class="grid grid-cols-2 gap-4">
						<div>
							<Label for="nl-wavesize" class="mb-1">{$t("Wave Size")}</Label>
							<Input id="nl-wavesize" type="number" bind:value={formWaveSize} min={1} />
						</div>
						<div>
							<Label for="nl-waveint" class="mb-1">{$t("Wave Interval (sec)")}</Label>
							<Input id="nl-waveint" type="number" bind:value={formWaveInterval} min={1} />
						</div>
					</div>
				</div>
			</AbstractCard>

			<!-- Token insertion toolbar + HTML content -->
			<AbstractCard class="max-w-none">
				<Heading tag="h5" class="mb-3">
					<i class="fa-solid fa-code me-2"></i>{$t("Content")}
				</Heading>

				<!-- Token insertion bar -->
				<div
					class="bg-depth mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 p-2 dark:border-gray-700"
				>
					<span class="mr-1 text-xs font-medium text-gray-500 dark:text-gray-400">{$t("Insert token:")}</span>
					{#each tokens as tok (tok.key)}
						<button
							type="button"
							class="inline-flex cursor-pointer items-center rounded border border-blue-300 bg-blue-50 px-2
								py-1 font-mono
								text-xs text-blue-700
								transition-colors hover:bg-blue-100
								dark:border-blue-600 dark:bg-blue-900/30
								dark:text-blue-300 dark:hover:bg-blue-900/60"
							title={`Insert {{${tok.key}}} into ${activeField}`}
							onclick={() => insertToken(tok.key)}
						>
							{'{{' + tok.key + '}}'}
						</button>
					{/each}
					<span class="ml-auto text-xs text-gray-400">
						{$t("inserting into:")} <Badge color="gray" class="ml-1">{activeField}</Badge>
					</span>
				</div>

				<div class="space-y-4">
					<div>
						<Label for="nl-html" class="mb-1">{$t("HTML Content")}</Label>
						<textarea
							id="nl-html"
							rows="14"
							class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 font-mono text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
							placeholder="<h1>Hello Jane!</h1>..."
							bind:value={formHtml}
							bind:this={htmlEl}
							onfocus={() => (activeField = 'html')}
						></textarea>
					</div>
					<div>
						<Label for="nl-text" class="mb-1">{$t("Plain-Text Fallback (optional)")}</Label>
						<textarea
							id="nl-text"
							rows="4"
							class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 font-mono text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
							placeholder={$t("Hello Jane!...")}
							bind:value={formText}
							bind:this={textEl}
							onfocus={() => (activeField = 'text')}
						></textarea>
						<p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
							{$t("Leave empty to auto-generate the plain-text part from the HTML body.")}
						</p>
					</div>
				</div>
			</AbstractCard>

			<!-- Actions -->
			<div class="flex gap-3">
				<Button onclick={save} disabled={saving || !formName || !formSubject || !formHtml}>
					{#if saving}<Spinner size="4" class="mr-2" />{/if}
					{isEditing ? $t('Save Changes') : $t('Create Newsletter')}
				</Button>
				<Button color="alternative" onclick={() => push('/Newsletters')}>{$t("Cancel")}</Button>
			</div>
		</div>

		<!-- ─── Right column: Live preview ─────────────────────── -->
		<div class="space-y-5">
			<AbstractCard class="max-w-none">
				<div class="mb-3 flex items-center justify-between">
					<Heading tag="h5">
						<i class="fa-solid fa-eye me-2"></i>{$t("Live Preview")}
					</Heading>
					{#if isEditing}
						<Button
							size="xs"
							color="alternative"
							onclick={loadServerPreview}
							disabled={serverPreviewLoading}
						>
							<i class="fa-solid fa-server mr-1 h-3.5 w-3.5"></i>
							{serverPreviewLoading ? $t('Loading…') : $t('Server Preview')}
						</Button>
					{/if}
				</div>

				<div class="mb-1 text-sm text-gray-500 dark:text-gray-400">{$t("Subject:")}</div>
				<div
					class="mb-3 rounded-lg border border-gray-200 bg-depth px-3 py-2 font-medium text-gray-900 dark:border-gray-700 dark:text-white"
				>
					{#if previewSubject}
						{previewSubject}
					{:else}
						<span class="italic text-gray-400">{$t("No subject yet")}</span>
					{/if}
				</div>

				<div class="mb-1 text-sm text-gray-500 dark:text-gray-400">{$t("Body:")}</div>
				{#if serverPreviewHtml}
					<!-- Full server-rendered preview -->
					<iframe
						title={$t("Server-rendered newsletter preview")}
						srcdoc={serverPreviewHtml}
						class="w-full rounded-lg border bg-white"
						style="min-height:450px;"
						sandbox="allow-same-origin"
					></iframe>
					<button
						class="mt-2 text-xs text-blue-600 hover:underline"
						onclick={() => (serverPreviewHtml = '')}
					>
						{$t("Back to inline preview")}
					</button>
				{:else if formHtml}
					<!-- Inline preview with mock substitution + master template wrapping -->
					<iframe
						title={$t("Inline newsletter preview")}
						srcdoc={previewHtmlContent}
						class="w-full rounded-lg border bg-white"
						style="min-height:450px;"
						sandbox="allow-same-origin"
					></iframe>
				{:else}
					<div
						class="flex h-48 items-center justify-center rounded-lg border-2 border-dashed border-gray-300 text-gray-400 dark:border-gray-600"
					>
						{$t("Start typing HTML content to see a preview")}
					</div>
				{/if}
			</AbstractCard>

			<!-- Token reference -->
			<AbstractCard class="max-w-none">
				<Heading tag="h6" class="mb-2">
					<i class="fa-solid fa-circle-info me-2"></i>{$t("Available Tokens")}
				</Heading>
				<div class="space-y-1 text-sm text-gray-600 dark:text-gray-400">
					{#each tokens as tok (tok.key)}
						<div class="flex items-center justify-between">
							<code class="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-gray-800"
								>{'{{' + tok.key + '}}'}</code
							>
							<span class="text-xs text-gray-400">→ {tok.preview}</span>
						</div>
					{/each}
				</div>
				<p class="mt-2 text-xs text-gray-400">
					{$t("Tokens are replaced per-recipient at send time. The preview shows sample data.")}
				</p>
			</AbstractCard>
		</div>
	</div>
{/if}
